/**
 * collapseQuoteRegions - wrap the quoted/threaded portion of an email
 * body in a collapsible ``<details>`` element so the conversation
 * thread reads cleanly without a 40 KB wall of replied-to history on
 * every reply.
 *
 * Detection strategy (in priority order):
 *   1. Known wrapper classes/elements. Mainstream mail clients emit
 *      identifiable markers around the quoted block:
 *        - <div class="gmail_quote">  / gmail_attr
 *        - <div class="OutlookMessageHeader">
 *        - <div class="WordSection*"> wrappers (Outlook desktop)
 *        - <div class="yahoo_quoted">
 *        - <div class="moz-cite-prefix">  / blockquote type="cite"
 *      The first occurrence wins so we collapse from "Original
 *      Message" / "On X wrote:" markers DOWN, not the customer's
 *      reply itself.
 *   2. Plain-text markers when no wrapper is found:
 *        - "On <date>, <person> wrote:"
 *        - "From: <person>\nSent: <date>\nTo: <addr>"   (Outlook)
 *        - "-----Original Message-----"
 *
 * A bare ``<blockquote>`` is deliberately NOT a marker. It is a real
 * authoring element: the composer's quote button, markdown ``>``
 * blocks, and API-posted bodies all produce one, and an authored
 * quote regularly has the writer's own text continuing BELOW it -
 * collapsing from the blockquote down swallowed that text (ticket
 * RF6, 2026-06-11). Prod data backs the removal: every native
 * inbound email that quoted a thread carried one of the wrapper
 * classes or attribution lines above (0 of 36 relied on a bare
 * blockquote alone), so dropping the pattern costs real email
 * nothing. Email clients that quote with a bare blockquote still
 * collapse via their attribution line ("On ... wrote:").
 *
 * Safety properties:
 *   - The trimmed content is NEVER hidden silently. The collapsed
 *     region renders inside a <details><summary>...</summary>...
 *     </details>, which the browser exposes as a one-click expand.
 *   - When detection misfires (no marker matched OR the marker is in
 *     the customer's own reply), the input HTML is returned
 *     unchanged or with an obvious "Show trimmed content" affordance
 *     - never lost.
 *   - Implementation is regex-only (no DOM parsing) so it runs on
 *     the same string DOMPurify produces. Tradeoff: we can't handle
 *     tags split across lines / weird whitespace. Acceptable because
 *     the input has already been normalized by DOMPurify.
 */

// Patterns that match the OPENING token of a quoted region. Order
// matters: longer / more-specific patterns first so partial matches
// don't fire on the wider one. Each pattern includes the start of
// the wrapper so we collapse from THAT point down.
//
// All patterns use ``(?<head>...)`` capture groups so consumers can
// see where the cut happened during debugging.
const QUOTE_PATTERNS: RegExp[] = [
  // FreshDesk's reply wrapper. Always emitted around the quoted-
  // history block on imported FD tickets - both the <div> shell and
  // the inner <blockquote> carry the class. Listed first so the cut
  // lands at the outer <div>, before the inner blockquote duplicates
  // the marker.
  /<(?:div|blockquote)[^>]*class="?freshdesk_quote"?[^>]*>/i,
  // Outlook desktop quote-header wrapper. NOTE: ``MsoNormal`` was
  // here originally and had to come out - it's Outlook's DEFAULT
  // paragraph class, present on every paragraph an Outlook reply
  // composes (including the agent's own text), so matching it cut
  // the body the moment the agent typed a single word in Outlook.
  // ``OutlookMessageHeader`` is the actual quote-block marker.
  /<div[^>]*class="?OutlookMessageHeader"?[^>]*>/i,
  /<div[^>]*class="?WordSection\d+"?[^>]*>/i,
  // Outlook Mobile (iOS / Android Outlook app) wraps the quoted
  // thread in a div with this id. The inner content also carries
  // class="ms-outlook-mobile-reference-message" but the container id
  // is the cleanest cut point because it sits OUTSIDE the new reply
  // and the user's signature, encompassing the entire quoted block.
  /<div[^>]*id="?mail-editor-reference-message-container"?[^>]*>/i,
  /<div[^>]*class="?gmail_quote(?:_container)?"?[^>]*>/i,
  /<div[^>]*class="?gmail_attr"?[^>]*>/i,
  /<div[^>]*class="?yahoo_quoted"?[^>]*>/i,
  /<div[^>]*class="?moz-cite-prefix"?[^>]*>/i,
  // Apple Mail uses blockquote type="cite" as the quote wrapper.
  // NOTE: a generic <blockquote> pattern used to follow this one and
  // had to come out - see the header comment. Only the attributed
  // (type="cite") form is an email-quote marker.
  /<blockquote[^>]*type=["']cite["'][^>]*>/i,
  // Plain-text "On <date>, <person> wrote:" - usually wrapped in
  // a <p> or <div>. We anchor to the "On ... wrote:" string.
  /<(?:p|div)[^>]*>\s*(?:On|Le|El)\s+[^<]{1,180}\s+wrote\s*:\s*<\/(?:p|div)>/i,
  // "-----Original Message-----" (Outlook old-style separator)
  /-{3,}\s*Original Message\s*-{3,}/i,
];

/**
 * Find the byte index where the quoted region begins, or -1 if none.
 *
 * Returns the EARLIEST match across all patterns whose preceding slice
 * has at least some visible text. Wrappers like ``WordSection1`` are
 * regularly used to wrap the ENTIRE comment body (including the
 * agent's own reply text); cutting at position 0 in that case would
 * leave nothing visible before the collapsed region, so we skip
 * matches with an empty "before" slice and keep looking for an inner
 * marker (e.g. a nested ``freshdesk_quote``). When no match has any
 * visible text before it, return -1 - the input renders unchanged.
 */
function findQuoteStart(html: string): number {
  const positions: number[] = [];
  for (const pattern of QUOTE_PATTERNS) {
    const m = pattern.exec(html);
    if (m && m.index >= 0) positions.push(m.index);
  }
  positions.sort((a, b) => a - b);
  for (const pos of positions) {
    const before = html.slice(0, pos);
    if (before.replace(/<[^>]+>/g, "").trim().length > 0) {
      return pos;
    }
  }
  return -1;
}

/**
 * Wrap the quoted/threaded portion of ``html`` in a <details> block
 * so the new reply is visible by default and the prior thread sits
 * one click away.
 *
 * If no quote marker is found, returns ``html`` unchanged.
 *
 * Output shape (when a quote IS detected):
 *
 *   <reply text...>
 *   <details class="buyist-quoted-thread">
 *     <summary aria-label="Show trimmed content">...</summary>
 *     <div class="buyist-quoted-thread-body">
 *       <quoted-original-content>
 *     </div>
 *   </details>
 *
 * The summary contents are a CSS-styled "..." pill so the
 * affordance reads as a Freshdesk-style inline expander rather than
 * the default browser triangle.
 */
export function collapseQuoteRegions(html: string): string {
  const cut = findQuoteStart(html);
  if (cut === -1) return html;

  const before = html.slice(0, cut);
  const quoted = html.slice(cut);

  // Empty quoted region after a sliver of whitespace - bail. Hides
  // the "..." button for emails where the marker matched a stray
  // empty wrapper (e.g. an Outlook-template empty WordSection).
  // (The "nothing visible before the cut" case is filtered inside
  // findQuoteStart so we never select such a cut to begin with.)
  if (quoted.replace(/<[^>]+>/g, "").trim().length === 0) {
    return html;
  }

  return (
    before +
    '<details class="buyist-quoted-thread" data-testid="quoted-thread">' +
    '<summary class="buyist-quoted-thread-toggle" ' +
    'aria-label="Show trimmed content">' +
    '<span aria-hidden="true">...</span>' +
    "</summary>" +
    '<div class="buyist-quoted-thread-body">' +
    quoted +
    "</div>" +
    "</details>"
  );
}
